/**
 * Картинка поста. 09-blockers-closed.md §9.2.
 *
 * Байты приходят по адресу `/api/v1/media/<media_id>`: `media_id` — адрес, а не содержимое,
 * и в подписываемый дескриптор он не входит (§Хребет данных 2). Поэтому картинка тут
 * загружается отдельным запросом, а не приезжает внутри модели рендера.
 *
 * Картинка ВСЕГДА первый блок (08-visuals.md §1.2ж), поэтому она прижата к верхнему краю
 * пузыря и другого варианта вёрстки у неё нет. Поле `show_caption_above_media` в дескрипторе
 * осталось — оно внутри подписываемых байтов (encode.ts), и его удаление стоило бы смены
 * версии канонизации, — но оно ИНЕРТНО: на порядок блоков не влияет ничто.
 */

import { type JSX, useState } from 'react';
import { IconEyeOff } from '../components/icons.tsx';
import styles from './MessagePreview.module.css';

export interface MediaBlockProps {
  readonly mediaId: string;
  /** ПРАВИЛО 8.3: часть подписываемого контента, а не настройка показа. */
  readonly hasSpoiler: boolean;
  /** Размеры из `MediaRef`. `null` — место под картинку не резервируется. */
  readonly width?: number | null;
  readonly height?: number | null;
}

export function MediaBlock({
  mediaId,
  hasSpoiler,
  width = null,
  height = null,
}: MediaBlockProps): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const [failed, setFailed] = useState(false);
  const covered = hasSpoiler && !revealed;

  return (
    <div className="tg-media">
      {/*
       * Соотношение сторон ставится ДО прихода байтов: карточка 1920×1080 — это 480×270 в
       * пузыре, то есть заметный скачок вёрстки на каждой перерисовке, пока картинка едет.
       * А перерисовок у панели много: тумблеры темы и режима.
       */}
      <div
        className={styles['mediaBox']}
        style={
          width !== null && height !== null
            ? { aspectRatio: `${String(width)} / ${String(height)}` }
            : undefined
        }
      >
        {/* alt пуст намеренно: картинка декоративна по отношению к тексту поста, а выдуманное
            описание в превью соврало бы про то, что увидит подписчик. */}
        <img
          className={styles['mediaImg']}
          src={`/api/v1/media/${mediaId}`}
          alt=""
          onError={() => {
            setFailed(true);
          }}
        />
        {/*
         * Не косметика. Предложение, не принятое за сутки, сметёт ночной `sweepOrphanedMedia`
         * (TTL 86 400 000 мс, `jobs/maintenance.ts`), и без этой ветки полоса сказала бы
         * «предложение ждёт решения», а пузырь показал бы пустое место без единого слова.
         */}
        {failed ? (
          <div className={styles['mediaFail']} data-testid="media-failed">
            Картинка не загрузилась: байты по адресу /api/v1/media/{mediaId} не отдались. Решение по
            такому посту принимать нельзя — обновите экран.
          </div>
        ) : null}
        {covered ? (
          <button
            type="button"
            className={styles['mediaCover']}
            onClick={() => {
              setRevealed(true);
            }}
          >
            <IconEyeOff />
            <span>Скрыто спойлером — нажмите, чтобы посмотреть</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
